import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { portalApi } from "../api/portalApi";
import { portalTokenStore } from "../api/tokenStore";
import { PortalAccount } from "../types/auth";

type PortalAuthState = {
  account: PortalAccount | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const PortalAuthContext = createContext<PortalAuthState | null>(null);

export function PortalAuthProvider({ children }: { children: React.ReactNode }) {
  const [account, setAccount] = useState<PortalAccount | null>(null);
  const [loading, setLoading] = useState(true);

  // Ao montar, tenta uma sessão silenciosa via cookie de refresh — cobre o F5 na página.
  useEffect(() => {
    let cancelled = false;
    portalApi
      .post("/portal/auth/refresh")
      .then(async (res) => {
        if (cancelled) return;
        portalTokenStore.set(res.data.accessToken);
        const me = await portalApi.get<PortalAccount>("/portal/auth/me");
        if (!cancelled) setAccount(me.data);
      })
      .catch(() => {
        if (!cancelled) setAccount(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await portalApi.post("/portal/auth/login", { email, password });
    portalTokenStore.set(res.data.accessToken);
    const me = await portalApi.get<PortalAccount>("/portal/auth/me");
    setAccount(me.data);
  }, []);

  const logout = useCallback(async () => {
    try {
      await portalApi.post("/portal/auth/logout");
    } finally {
      portalTokenStore.set(null);
      setAccount(null);
    }
  }, []);

  const value = useMemo(() => ({ account, loading, login, logout }), [account, loading, login, logout]);

  return <PortalAuthContext.Provider value={value}>{children}</PortalAuthContext.Provider>;
}

export function usePortalAuth(): PortalAuthState {
  const ctx = useContext(PortalAuthContext);
  if (!ctx) throw new Error("usePortalAuth precisa estar dentro de <PortalAuthProvider>");
  return ctx;
}
